import { supabase } from '../lib/supabase';

class SessionManagementService {
  /**
   * Generate a device fingerprint based on browser characteristics
   */
  generateDeviceFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);
    
    const fingerprint = {
      canvas: canvas.toDataURL(),
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: screen.colorDepth
    };
    
    // Create hash of fingerprint data
    const fingerprintString = JSON.stringify(fingerprint);
    return this.simpleHash(fingerprintString);
  }

  /**
   * Simple hash function for fingerprint
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get device type
   */
  getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  /**
   * Get device name (browser + OS)
   */
  getDeviceName() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';

    // Detect browser
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';

    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    return `${browser} on ${os}`;
  }

  /**
   * Get user's IP address and location (via external API)
   */
  async getLocationData() {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return {
        ip: data.ip,
        city: data.city,
        country: data.country_name
      };
    } catch (error) {
      console.error('Error fetching location:', error);
      return {
        ip: null,
        city: null,
        country: null
      };
    }
  }

  /**
   * Check if user can create a new session
   */
  async canCreateSession(userId, deviceFingerprint) {
    try {
      const { data, error } = await supabase
        .rpc('can_create_session', {
          p_user_id: userId,
          p_device_fingerprint: deviceFingerprint
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error checking session:', error);
      throw error;
    }
  }

  /**
   * Create or update a session
   */
  async createSession(userId) {
    try {
      const deviceFingerprint = this.generateDeviceFingerprint();
      const location = await this.getLocationData();
      
      // Check if session can be created
      const canCreate = await this.canCreateSession(userId, deviceFingerprint);
      
      if (!canCreate.allowed) {
        return {
          success: false,
          reason: canCreate.reason,
          message: canCreate.message
        };
      }

      const sessionData = {
        user_id: userId,
        session_token: crypto.randomUUID(),
        device_fingerprint: deviceFingerprint,
        ip_address: location.ip,
        user_agent: navigator.userAgent,
        device_name: this.getDeviceName(),
        device_type: this.getDeviceType(),
        location_city: location.city,
        location_country: location.country,
        is_active: true
      };

      // Upsert session (update if device exists, insert if new)
      const { data, error } = await supabase
        .from('user_sessions')
        .upsert(sessionData, {
          onConflict: 'user_id,device_fingerprint'
        })
        .select()
        .single();

      if (error) throw error;

      // Log the activity
      await this.logActivity(userId, data.id, 'login', location.ip, location.city, location.country, deviceFingerprint);

      // Store session token in localStorage
      localStorage.setItem('session_token', data.session_token);

      return {
        success: true,
        session: data
      };
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  /**
   * Update session activity (heartbeat)
   */
  async updateSessionActivity() {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) return;

      const { error } = await supabase
        .from('user_sessions')
        .update({
          last_activity_at: new Date().toISOString()
        })
        .eq('session_token', sessionToken)
        .eq('is_active', true);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating session activity:', error);
    }
  }

  /**
   * End current session (logout)
   */
  async endSession() {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) return;

      const { data: session } = await supabase
        .from('user_sessions')
        .select('user_id, id')
        .eq('session_token', sessionToken)
        .single();

      if (session) {
        // Mark session as inactive
        await supabase
          .from('user_sessions')
          .update({ is_active: false })
          .eq('session_token', sessionToken);

        // Log logout
        await this.logActivity(session.user_id, session.id, 'logout', null, null, null, null);
      }

      localStorage.removeItem('session_token');
    } catch (error) {
      console.error('Error ending session:', error);
    }
  }

  /**
   * Get active sessions for current user
   */
  async getActiveSessions(userId) {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('last_activity_at', { ascending: false });

      if (error) throw error;

      return data.map(session => ({
        id: session.id,
        deviceName: session.device_name,
        deviceType: session.device_type,
        location: `${session.location_city || 'Unknown'}, ${session.location_country || 'Unknown'}`,
        ipAddress: session.ip_address,
        lastActivity: session.last_activity_at,
        createdAt: session.created_at,
        isCurrent: session.session_token === localStorage.getItem('session_token')
      }));
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId) {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_active: false })
        .eq('id', sessionId);

      if (error) throw error;
    } catch (error) {
      console.error('Error revoking session:', error);
      throw error;
    }
  }

  /**
   * Get account sharing alerts
   */
  async getAccountAlerts(userId) {
    try {
      const { data, error } = await supabase
        .from('account_sharing_alerts')
        .select('*')
        .eq('user_id', userId)
        .eq('resolved', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(alert => ({
        id: alert.id,
        type: alert.alert_type,
        severity: alert.severity,
        details: alert.details,
        createdAt: alert.created_at
      }));
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  }

  /**
   * Log session activity
   */
  async logActivity(userId, sessionId, activityType, ipAddress, city, country, deviceFingerprint) {
    try {
      await supabase
        .from('session_activity_log')
        .insert({
          user_id: userId,
          session_id: sessionId,
          activity_type: activityType,
          ip_address: ipAddress,
          location_city: city,
          location_country: country,
          device_fingerprint: deviceFingerprint
        });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  /**
   * Start session heartbeat (update activity every 5 minutes)
   */
  startHeartbeat() {
    // Update immediately
    this.updateSessionActivity();
    
    // Then update every 5 minutes
    this.heartbeatInterval = setInterval(() => {
      this.updateSessionActivity();
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Stop session heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
  }
}

export default new SessionManagementService();
